import type { ClipboardEvent, KeyboardEvent, SyntheticEvent } from 'react';
import { useCallback, useRef, useState } from 'react';
import type { FieldValues, RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { mergeTagValues, splitCommaSeparatedValues } from '@/utils/tagFieldUtils';

import { VNDropdown } from '../VNDropdown';

type Props = {
  name: string;
  label: string;
  options?: any[];
  rules?: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  required?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
};

const VNTagAutocomplete = ({
  name,
  label,
  options = [],
  rules,
  required = false,
  disabled = false,
  allowClear = false,
}: Props) => {
  const { getValues, setValue } = useFormContext();
  const [inputValue, setInputValue] = useState('');
  const skipAutocompleteChangeRef = useRef(false);

  const commitTags = useCallback(
    (text: string) => {
      const incoming = splitCommaSeparatedValues(text);
      if (incoming.length === 0) {
        return;
      }

      skipAutocompleteChangeRef.current = true;
      const current = getValues(name);
      const currentArr = Array.isArray(current) ? current : [];
      setValue(name, mergeTagValues(currentArr, incoming), { shouldDirty: true });
      setInputValue('');
      queueMicrotask(() => {
        skipAutocompleteChangeRef.current = false;
      });
    },
    [getValues, name, setValue]
  );

  const handleAutocompleteChange = useCallback(
    (newValue: unknown) => {
      if (skipAutocompleteChangeRef.current) {
        return;
      }

      const arr = Array.isArray(newValue) ? newValue.map(String) : [];
      setValue(name, mergeTagValues([], arr), { shouldDirty: true });
    },
    [name, setValue]
  );

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string, reason: string) => {
    if (reason === 'reset') {
      setInputValue('');
      return;
    }

    if (reason !== 'input') {
      return;
    }

    if (newInputValue.includes(',')) {
      const parts = splitCommaSeparatedValues(newInputValue);
      const endsWithComma = /,\s*$/.test(newInputValue);

      if (endsWithComma && parts.length > 0) {
        commitTags(newInputValue);
        return;
      }

      if (parts.length > 1) {
        const incomplete = parts[parts.length - 1] ?? '';
        const complete = parts.slice(0, -1);

        skipAutocompleteChangeRef.current = true;
        const current = getValues(name);
        const currentArr = Array.isArray(current) ? current : [];
        setValue(name, mergeTagValues(currentArr, complete), { shouldDirty: true });
        setInputValue(incomplete);
        queueMicrotask(() => {
          skipAutocompleteChangeRef.current = false;
        });
        return;
      }
    }

    setInputValue(newInputValue);
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (!pastedText.includes(',')) {
      return;
    }

    e.preventDefault();
    commitTags(pastedText);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      e.stopPropagation();
      commitTags(inputValue);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      commitTags(inputValue);
    }
  };

  return (
    <VNDropdown
      name={name}
      label={label}
      options={options}
      multiple
      hideOptions
      searchable="autocomplete"
      rules={rules}
      required={required}
      disabled={disabled}
      allowClear={allowClear}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onValueChange={handleAutocompleteChange}
      inputProps={{ onPaste: handlePaste, onKeyDown: handleKeyDown, onBlur: handleBlur }}
    />
  );
};

export default VNTagAutocomplete;
